// Delivery-receipt machinery — the client side of PROTOCOL.md §5a.
//
// Every `ControllerSocket.publish()` mints a delivery key and returns
// a `PublishReceipt`. The receipt's status begins `pending`; when the
// broker echoes the envelope back to its sender (with the same key),
// the receipt flips to `sent`. If no echo arrives inside the retry
// budget, the socket resends the envelope with `attempt` incremented;
// after the final attempt the receipt flips to `failed`.
//
// Callers that don't care can ignore the receipt — `publish()` is
// still effectively fire-and-forget. The receipt exists for UI
// surfaces that want a spinner→check affordance and for tests that
// assert delivery semantics.

export type ReceiptStatus = 'pending' | 'sent' | 'failed';

export interface PublishReceipt {
  /** The delivery key on the wire. Stable across retries. */
  readonly key: string;
  /** Current state. */
  status(): ReceiptStatus;
  /** Subscribe to state changes; returns an unsubscribe handle. */
  onChange(listener: (status: ReceiptStatus) => void): () => void;
  /**
   * Promise that resolves when status reaches `sent`, rejects when
   * it reaches `failed`, and never settles otherwise.
   */
  done(): Promise<void>;
  /** Number of completed attempts so far. 1 = first publish sent. */
  attempts(): number;
  /** Abandon waiting; future echoes are ignored and status flips to failed. */
  cancel(): void;
}

export interface ReceiptRetryPolicy {
  /**
   * Backoff delays in ms before each retry. The number of entries
   * sets the max attempt count: 3 entries → up to 4 attempts (initial
   * + 3 retries). Default [3000, 6000, 12000].
   */
  delaysMs: number[];
}

export const DEFAULT_RETRY_POLICY: ReceiptRetryPolicy = {
  delaysMs: [3000, 6000, 12000],
};

interface ReceiptHooks {
  /** Resend the envelope with `attempt` set to the current attempt counter. */
  resend(attempt: number): void;
  /** Drop the receipt from the socket's ledger. */
  release(): void;
}

interface Scheduler {
  setTimeout(fn: () => void, ms: number): ReturnType<typeof setTimeout>;
  clearTimeout(handle: ReturnType<typeof setTimeout>): void;
}

/**
 * Internal receipt implementation. Owned by the socket; exposed to
 * callers through the `PublishReceipt` interface.
 */
export class ReceiptImpl implements PublishReceipt {
  private state: ReceiptStatus = 'pending';
  private attemptCount = 1;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Set<(status: ReceiptStatus) => void>();
  private resolveDone: (() => void) | null = null;
  private rejectDone: ((err: Error) => void) | null = null;
  private readonly donePromise: Promise<void>;

  constructor(
    readonly key: string,
    private readonly hooks: ReceiptHooks,
    private readonly policy: ReceiptRetryPolicy,
    private readonly scheduler: Scheduler,
  ) {
    this.donePromise = new Promise<void>((resolve, reject) => {
      this.resolveDone = resolve;
      this.rejectDone = reject;
    });
    // Swallow unhandled rejections — callers opt in via `done()`.
    this.donePromise.catch(() => undefined);
    this.armRetry();
  }

  status(): ReceiptStatus {
    return this.state;
  }

  attempts(): number {
    return this.attemptCount;
  }

  onChange(listener: (status: ReceiptStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  done(): Promise<void> {
    return this.donePromise;
  }

  cancel(): void {
    if (this.state !== 'pending') return;
    this.fail();
  }

  /** Called by the socket when the broker echoes this key back. */
  markSent(): void {
    if (this.state !== 'pending') return;
    this.clearTimer();
    this.transition('sent');
    if (this.resolveDone) this.resolveDone();
    this.hooks.release();
  }

  private armRetry(): void {
    // attemptCount-1 indexes the policy.delaysMs entry for the *next* retry.
    // If we've exhausted the policy, schedule a final timeout to fail.
    const idx = this.attemptCount - 1;
    if (idx >= this.policy.delaysMs.length) {
      // No more retries; wait the last delay one more time before failing,
      // so a late echo can still rescue the receipt.
      const tail = this.policy.delaysMs[this.policy.delaysMs.length - 1] ?? 5000;
      this.timer = this.scheduler.setTimeout(() => this.fail(), tail);
      return;
    }
    const delay = this.policy.delaysMs[idx]!;
    this.timer = this.scheduler.setTimeout(() => {
      this.timer = null;
      if (this.state !== 'pending') return;
      this.attemptCount += 1;
      this.hooks.resend(this.attemptCount);
      this.armRetry();
    }, delay);
  }

  private fail(): void {
    this.clearTimer();
    this.transition('failed');
    if (this.rejectDone) {
      this.rejectDone(
        new Error(`delivery failed after ${this.attemptCount} attempt(s): ${this.key}`),
      );
    }
    this.hooks.release();
  }

  private transition(next: ReceiptStatus): void {
    this.state = next;
    for (const l of this.listeners) {
      try {
        l(next);
      } catch {
        // listener errors do not break the broadcast
      }
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      this.scheduler.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
